import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom'; // Ensure Link is imported
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import axios from 'axios';
import Header from '../../components/Header'; // Adjust the import path as needed
import { useTheme } from "@mui/material";

const SearchPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchResults, setSearchResults] = useState({ results: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);

  useEffect(() => {
    const searchTerm = new URLSearchParams(location.search).get('q');
    if (searchTerm) {
      fetchSearchResults(`https://cheebo-server.neuralbey.com/dashboard/store/searchProduct/?search=${searchTerm}`);
    }
  }, [location.search]);

  const fetchSearchResults = async (url) => {
    try {
      setLoading(true);
      const response = await axios.get(url);
      setSearchResults(response.data);
      setNextPage(response.data.next);
      setPrevPage(response.data.previous);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setError("An error occurred while fetching search results. Please try again.");
      setLoading(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    {
        field: "image", // New image field
        headerName: "Image",
        width: 100,
        renderCell: ({ row }) => (
          <img src={`https://cheebo-server.neuralbey.com${row.image}`} alt={row.title} style={{ width: '50px', height: '50px', borderRadius: '4px' }} />
        ),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      renderCell: (params) => (
        <Link 
          to={`/store/productDetailsList/${params.row.id}`} // Adjusted route to include product_id in URL
          style={{ textDecoration: 'none', color: colors.greenAccent[300] }} // Style for link
        >
          {params.value}
        </Link>
      ),
    },
    { field: 'price', headerName: 'Price', flex: 0.5 },
    { field: 'category', headerName: 'Category', flex: 0.5 },
    { field: 'subCategory1', headerName: 'Sub-Category 1', flex: 0.5 },
    { field: 'subCategory2', headerName: 'Sub-Category 2', flex: 0.5 },
    { field: 'seller', headerName: 'Seller', flex: 1 },
    { 
      field: 'availability', 
      headerName: 'Availability', 
      flex: 0.5,
      renderCell: ({ row }) => (
        <Box
          width="60%"
          m="0 auto"
          p="5px"
          display="flex"
          justifyContent="center"
          backgroundColor={
            row.availability === true
              ? colors.greenAccent[600]
              : colors.redAccent[700]
          }
          borderRadius="4px"
        >
          {row.availability === true ? "In Stock" : "Out of Stock"}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="SEARCH RESULTS" subtitle="Product Search Results" />
      
      {/* Add Product Button */}
      <Box mb={2}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/store/addProduct')}
        >
          Add a Product
        </Button>
      </Box>

      {/* Display total and current count */}
      <Typography variant="h6">
        Total Products: {searchResults.count} | Currently Displayed: {searchResults.results.length}
      </Typography>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={searchResults.results}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
        />
        
        {/* Pagination Controls */}
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button 
            variant="contained" 
            disabled={!prevPage} 
            onClick={() => fetchSearchResults(prevPage)}
          >
            Previous
          </Button>
          
          <Button 
            variant="contained" 
            disabled={!nextPage} 
            onClick={() => fetchSearchResults(nextPage)}
          >
            Next
          </Button>
        </Box>
        
        {loading && <Typography>Loading...</Typography>}
        
      </Box>
    </Box>
  );
};

export default SearchPage;